import { lazy } from 'react';

/**
 * A wrapper around React.lazy that handles chunk loading errors gracefully.
 * When a chunk fails to load (e.g., after a new deployment), it will:
 * 1. Try to reload the chunk once
 * 2. If that fails, force a full page reload to get the latest version
 * 
 * This prevents white screens when users have stale cached HTML but the
 * JS chunks have been replaced by a new deployment.
 */
export function lazyWithRetry(componentImport, componentName = 'Component') {
  return lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
    );

    try {
      const component = await componentImport();
      // Successfully loaded - reset the refresh flag
      window.sessionStorage.setItem('page-has-been-force-refreshed', 'false');
      return component;
    } catch (error) {
      // Check if this is a chunk loading error
      const isChunkLoadError =
        error.message?.includes('Failed to fetch dynamically imported module') ||
        error.message?.includes('Loading chunk') ||
        error.message?.includes('Loading CSS chunk') ||
        error.message?.includes('not a valid JavaScript MIME type') ||
        error.message?.includes("Importing a module script failed") ||
        error.name === 'ChunkLoadError';

      if (isChunkLoadError && !pageHasAlreadyBeenForceRefreshed) {
        // Mark that we're about to refresh to prevent infinite loops
        window.sessionStorage.setItem('page-has-been-force-refreshed', 'true');
        console.warn(`[lazyWithRetry] Chunk load failed for ${componentName}, reloading page...`);
        // Force reload from server, not from cache
        window.location.reload();
        // Return a never-resolving promise to prevent React from rendering while reloading
        return new Promise(() => {});
      }

      // If we've already tried refreshing or it's not a chunk error, throw
      console.error(`[lazyWithRetry] Failed to load ${componentName}:`, error);
      throw error;
    }
  });
}

/**
 * Global handler for chunk loading errors that may occur outside of lazy loading.
 * Call this once in your app entry point (main.jsx).
 */
export function setupChunkErrorHandler() {
  // Handle errors from dynamic imports that fail
  window.addEventListener('error', (event) => {
    const isChunkError =
      event.message?.includes('Loading chunk') ||
      event.message?.includes('Loading CSS chunk') ||
      event.message?.includes('not a valid JavaScript MIME type') ||
      event.message?.includes('Importing a module script failed') ||
      event.filename?.includes('/assets/');
    
    if (isChunkError) {
      const pageHasAlreadyBeenForceRefreshed = JSON.parse(
        window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
      );
      
      if (!pageHasAlreadyBeenForceRefreshed) {
        window.sessionStorage.setItem('page-has-been-force-refreshed', 'true');
        console.warn('[ChunkErrorHandler] Chunk load error detected, reloading...');
        window.location.reload();
      }
    }
  });

  // Handle unhandled promise rejections (dynamic imports return promises)
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const isChunkError =
      reason?.message?.includes('Failed to fetch dynamically imported module') ||
      reason?.message?.includes('Loading chunk') ||
      reason?.message?.includes('not a valid JavaScript MIME type') ||
      reason?.message?.includes('Importing a module script failed') ||
      reason?.name === 'ChunkLoadError';
    
    if (isChunkError) {
      const pageHasAlreadyBeenForceRefreshed = JSON.parse(
        window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
      );
      
      if (!pageHasAlreadyBeenForceRefreshed) {
        event.preventDefault(); // Prevent the error from being logged to console
        window.sessionStorage.setItem('page-has-been-force-refreshed', 'true');
        console.warn('[ChunkErrorHandler] Dynamic import failed, reloading...');
        window.location.reload();
      }
    }
  });
}

export default lazyWithRetry;















